<template>
  <div class="container mx-auto max-w-7xl px-4 py-16">
    <div class="mb-12 text-center">
      <h1 class="mb-6 font-serif text-3xl text-lk-green md:text-4xl">Planes de Suscripción</h1>
      <p class="mx-auto max-w-3xl text-lg text-gray-600">
        <span class="font-semibold">¿Qué plan se adapta mejor a tus necesidades?</span>
        <br />
        Queremos saber tu opinión para entregarte un mejor servicio.
      </p>
    </div>

    <div class="grid gap-8 md:grid-cols-3">
      <!-- Basic Plan -->
      <button
        class="flex flex-col overflow-hidden rounded-lg border bg-white shadow-lg transition-all duration-200"
        :class="[
          selectedPlan === 'basic' ? 'scale-100 border-2 border-lk-light-green md:scale-105' : 'border-gray-200',
        ]"
        @click="selectPlan('basic')"
      >
        <div
          class="p-6 text-center"
          :class="[selectedPlan === 'basic' ? '-mx-px -mt-px bg-lk-light-green' : 'bg-lk-warm-gray']"
        >
          <BoltIcon
            class="ml-1 inline-block size-4"
            :class="[selectedPlan === 'basic' ? 'text-white' : 'text-gray-700']"
          />
          <h2
            class="font-serif-noto text-2xl font-medium"
            :class="[selectedPlan === 'basic' ? 'text-white' : 'text-gray-700']"
          >
            Basic
          </h2>
          <p
            class="mt-2 text-sm font-light"
            :class="[selectedPlan === 'basic' ? 'text-white' : 'text-gray-700']"
          >
            Cuando necesitas solo un poquito más.
          </p>
        </div>
        <div class="border-b border-gray-200 p-6 text-center">
          <span
            class="text-4xl font-bold"
            :class="[selectedPlan === 'basic' ? 'text-lk-light-green' : 'text-gray-700']"
          >
            $9.990
          </span>
          <span class="ml-1 text-gray-600">/mes</span>
        </div>
        <div class="grow p-6">
          <ul class="space-y-4">
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'basic' ? 'text-lk-light-green' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'basic' ? 'text-gray-600' : 'text-gray-600']"
              >
                <!-- Este plan incluye dos despachos gratis al mes en la RM. -->
                Obtén $20.000 en créditos para despachos.
              </span>
            </li>
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'basic' ? 'text-lk-light-green' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'basic' ? 'text-gray-600' : 'text-gray-600']"
              >
                Entérate antes que todos de los descuentos que vienen.
              </span>
            </li>
          </ul>
        </div>
        <div class="p-6">
          <button
            class="w-full rounded-md px-4 py-3 font-medium transition duration-200"
            :class="[
              selectedPlan === 'basic'
                ? 'bg-lk-light-green text-white'
                : 'bg-lk-warm-gray text-gray-700 hover:bg-lk-warm-gray-deep hover:text-white',
            ]"
            @click.stop.prevent="selectPlan('basic')"
          >
            {{ selectedPlan === 'basic' ? 'Seleccionado' : 'Seleccionar' }}
          </button>
        </div>
      </button>

      <!-- Lokal+ Plan (Recommended) -->
      <button
        class="relative flex flex-col overflow-hidden rounded-lg border bg-white shadow-lg transition-all duration-200"
        :class="[selectedPlan === 'plus' ? 'scale-100 border-2 border-lk-green md:scale-105' : 'border-gray-200']"
        @click="selectPlan('plus')"
      >
        <div class="absolute right-0 top-0 z-10">
          <div
            class="rounded-bl-lg bg-lk-yellow px-4 py-1 text-xs font-bold uppercase"
            :class="[selectedPlan === 'plus' ? 'text-lk-green' : 'text-gray-700']"
          >
            Recomendado
          </div>
        </div>
        <div
          class="p-6 text-center"
          :class="[selectedPlan === 'plus' ? '-mx-px -mt-px bg-lk-green' : 'bg-lk-warm-gray']"
        >
          <RocketLaunchIcon
            class="ml-1 inline-block size-4"
            :class="[selectedPlan === 'plus' ? 'text-white' : 'text-gray-700']"
          />
          <h2
            class="font-serif-noto text-2xl font-medium"
            :class="[selectedPlan === 'plus' ? 'text-white' : 'text-gray-700']"
          >
            Plus
          </h2>
          <p
            class="mt-2 text-sm font-light"
            :class="[selectedPlan === 'plus' ? 'text-white' : 'text-gray-700']"
          >
            Cuando quieres crecer.
          </p>
        </div>
        <div class="border-b border-gray-200 p-6 text-center">
          <span
            class="text-4xl font-bold"
            :class="[selectedPlan === 'plus' ? 'text-lk-green' : 'text-gray-700']"
          >
            $19.990
          </span>
          <span class="ml-1 text-gray-600">/mes</span>
        </div>
        <div class="grow p-6">
          <ul class="space-y-4">
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'plus' ? 'text-lk-green' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'plus' ? 'text-gray-600' : 'text-gray-600']"
              >
                Obtén $40.000 en créditos para despachos.
              </span>
            </li>
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'plus' ? 'text-lk-green' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'plus' ? 'text-gray-600' : 'text-gray-600']"
              >
                <!-- Este plan incluye despachos gratis ilimitados en pedidos desde y hacia la RM (más de 290 marcas
                elegibles). -->
                Entérate antes que todos de los descuentos que vienen.
              </span>
            </li>
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'plus' ? 'text-lk-green' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'plus' ? 'text-gray-600' : 'text-gray-600']"
              >
                Reúnete con uno de nuestros asesores una vez al mes.
              </span>
            </li>
          </ul>
        </div>
        <div class="p-6">
          <button
            class="w-full rounded-md px-4 py-3 font-medium transition duration-200"
            :class="[
              selectedPlan === 'plus'
                ? 'bg-lk-green text-white hover:bg-lk-green-deep'
                : 'bg-lk-warm-gray text-gray-700 hover:bg-lk-warm-gray-deep hover:text-white',
            ]"
            @click.stop.prevent="selectPlan('plus')"
          >
            {{ selectedPlan === 'plus' ? 'Seleccionado' : 'Seleccionar' }}
          </button>
        </div>
      </button>

      <!-- Lokal Premium Plan -->
      <button
        class="flex flex-col overflow-hidden rounded-lg border bg-white shadow-lg transition-all duration-200"
        :class="[selectedPlan === 'premium' ? 'scale-100 border-2 border-lk-purple md:scale-105' : 'border-gray-200']"
        @click="selectPlan('premium')"
      >
        <div
          class="p-6 text-center"
          :class="[selectedPlan === 'premium' ? '-mx-px -mt-px bg-lk-purple' : 'bg-lk-warm-gray']"
        >
          <StarIcon
            class="ml-1 inline-block size-4"
            :class="[selectedPlan === 'premium' ? 'text-white' : 'text-gray-700']"
          />
          <h2
            class="font-serif-noto text-2xl font-medium"
            :class="[selectedPlan === 'premium' ? 'text-white' : 'text-gray-700']"
          >
            Premium
          </h2>
          <p
            class="mt-2 text-sm font-light"
            :class="[selectedPlan === 'premium' ? 'text-white' : 'text-gray-700']"
          >
            Cuando quieres más. De todo.
          </p>
        </div>
        <div class="border-b border-gray-200 p-6 text-center">
          <span
            class="text-4xl font-bold"
            :class="[selectedPlan === 'premium' ? 'text-lk-purple' : 'text-gray-700']"
          >
            $49.990
          </span>
          <span class="ml-1 text-gray-600">/mes</span>
        </div>
        <div class="grow p-6">
          <ul class="space-y-4">
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'premium' ? 'text-lk-purple' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'premium' ? 'text-gray-600' : 'text-gray-600']"
              >
                Accede a todos los beneficios del plan Plus.
              </span>
            </li>
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'premium' ? 'text-lk-purple' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'premium' ? 'text-gray-600' : 'text-gray-600']"
              >
                Obtén un 5% de descuento en todas las marcas nuevas que pruebes cada mes.
              </span>
            </li>
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'premium' ? 'text-lk-purple' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'premium' ? 'text-gray-600' : 'text-gray-600']"
              >
                Obtén un 3% de descuento para todos los productos de marcas que no sean nuevas.
              </span>
            </li>
            <li class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="mt-0.5 size-5 shrink-0"
                :class="[selectedPlan === 'premium' ? 'text-lk-purple' : 'text-gray-500']"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 
                  00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="ml-3"
                :class="[selectedPlan === 'premium' ? 'text-gray-600' : 'text-gray-600']"
              >
                Reúnete con uno de nuestros asesores semanalmente.
              </span>
            </li>
          </ul>
        </div>
        <div class="p-6">
          <button
            class="w-full rounded-md px-4 py-3 font-medium transition duration-200"
            :class="[
              selectedPlan === 'premium'
                ? 'bg-lk-purple text-white hover:bg-lk-purple'
                : 'bg-lk-warm-gray text-gray-700 hover:bg-lk-warm-gray-deep hover:text-white',
            ]"
            @click.stop.prevent="selectPlan('premium')"
          >
            {{ selectedPlan === 'premium' ? 'Seleccionado' : 'Seleccionar' }}
          </button>
        </div>
      </button>
    </div>
    <div
      v-if="selectedPlan"
      class="mt-10 text-center"
    >
      <button
        class="rounded-md bg-lk-green px-6 py-3 font-medium text-white transition duration-200 hover:bg-lk-green-deep"
        @click="handleSubscriptionDialogRequest"
      >
        Continuar con el plan {{ getPlanName }}
      </button>
    </div>
    <subscription-details-dialog
      v-model="showSubscriptionDialog"
      :selected-plan="selectedPlan"
      :amount="selectedPlan === 'basic' ? 9990 : selectedPlan === 'plus' ? 19990 : 49990"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { BoltIcon } from '@heroicons/vue/24/outline';
import { StarIcon, RocketLaunchIcon } from '@heroicons/vue/24/solid';
import useSessionStore from 'stores/user-store';
import useGeneralMenuStore from 'stores/general-menu-store';
import SubscriptionDetailsDialog from './subscription-details-dialog.vue';

type PlanType = 'basic' | 'plus' | 'premium';

const selectedPlan = ref<PlanType>('plus');
const showSubscriptionDialog = ref(false);

const sessionStore = useSessionStore();
const menuStore = useGeneralMenuStore();

function selectPlan(plan: PlanType) {
  console.log('selectPlan', plan);
  console.log('selectedPlan', selectedPlan.value);
  if (selectedPlan.value === plan) {
    showSubscriptionDialog.value = true;
  } else {
    selectedPlan.value = plan;
  }
}

function handleSubscriptionDialogRequest() {
  if (sessionStore.user.id) {
    showSubscriptionDialog.value = true;
  } else {
    menuStore.initialValueSignIn = true;
    menuStore.showUserRegisterDialog = true;
  }
}

const getPlanName = computed(() => {
  if (selectedPlan.value === 'basic') {
    return 'Basic';
  } else if (selectedPlan.value === 'plus') {
    return 'Plus';
  } else if (selectedPlan.value === 'premium') {
    return 'Premium';
  }
  return '';
});
</script>
